import { useState, useEffect } from 'react';
import { getCurrentWeekNumber, getWeekNumberFromDate, generateDocumentNumberForDate } from '../utils/qdfUtils';
import { qdfAPI } from '../../../services/api';

const DEFAULT_ASSET_DESCRIPTION = 'BGM System';
const DEFAULT_PM_DESCRIPTION = 'BGM System';
const LOCKED_PUBLIC_FIELDS = new Set(['assetTag', 'assetNumber', 'location', 'department', 'contractor', 'scStatus']);

export const useQDFForm = (initialMaintenanceType = 'corrective', editData = null, options = {}) => {
  const isPublicMode = Boolean(options?.isPublicMode);
  const publicPrefill = options?.publicPrefill || null;
  const shopConfig = options?.shopConfig;
  const assetTagDetails = shopConfig?.assetTagDetails || {};
  const assetNumberByTag = shopConfig?.assetNumberByTag || {};

  const [maintenanceType, setMaintenanceType] = useState(
    isPublicMode ? 'corrective' : (editData ? editData.maintenanceType : initialMaintenanceType)
  );
  
  // Get current date and time formatted for datetime-local input (preserving local timezone)
  const getCurrentDateTime = () => {
    const now = new Date();
    // Create date string in local timezone
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  
  // Convert ISO date to datetime-local format (preserving local time)
  const convertToDateTimeLocal = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    
    // Get local date and time components to avoid timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  
  const [formData, setFormData] = useState({
    ppmType: editData?.ppmType || '',
    scNumber: editData?.scNumber || '',
    scStatus: isPublicMode ? 'Active' : (editData?.scStatus || ''),
    creationDate: editData ? convertToDateTimeLocal(editData.creationDate) : getCurrentDateTime(),
    contractor: 'RGA',
    priority: editData?.priority || '',
    scDescription: editData?.scDescription || ((editData?.maintenanceType || initialMaintenanceType) === 'preventive' ? DEFAULT_PM_DESCRIPTION : ''),
    targetResponse: editData ? convertToDateTimeLocal(editData.targetResponse) : getCurrentDateTime(),
    targetResolution: editData ? convertToDateTimeLocal(editData.targetResolution) : getCurrentDateTime(),
    actualDate: editData ? convertToDateTimeLocal(editData.actualDate) : (initialMaintenanceType === 'corrective' ? getCurrentDateTime() : ''),
    actualResolution: editData ? convertToDateTimeLocal(editData.actualResolution) : (initialMaintenanceType === 'corrective' ? getCurrentDateTime() : ''),
    assetNumber: editData?.assetNumber || '',
    assetDescription: DEFAULT_ASSET_DESCRIPTION,
    department: editData?.department || '',
    assetTag: editData?.assetTag || '',
    location: editData?.location || '',
    weekNumber: editData?.weekNumber || (editData ? getWeekNumberFromDate(editData.creationDate) : getCurrentWeekNumber()),
    zone: editData?.zone || '',
    requesterName: editData?.requesterName || '',
    requesterContact: editData?.contactNumber || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingNumber, setGeneratingNumber] = useState(false);

  // Create form change handler with special handling for creation date and asset tag
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (isPublicMode && LOCKED_PUBLIC_FIELDS.has(name)) {
      return;
    }
    
    // Special handling for asset tag selection
    if (name === 'assetTag') {
      if (value) {
        // Asset tag selected - auto-fill related fields
        const assetDetails = assetTagDetails[value];
        const assetNumber = assetNumberByTag[value] || '';
        
        setFormData(prev => ({
          ...prev,
          [name]: value,
          // Auto-fill related fields based on selected asset tag
          assetNumber: assetNumber,
          location: assetDetails?.location || '',
          department: assetDetails?.department || ''
        }));
      } else {
        // Asset tag cleared - reset related fields
        setFormData(prev => ({
          ...prev,
          [name]: value,
          assetNumber: '',
          location: '',
          department: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // If creation date is changed and we're editing, regenerate document number
    if (!isPublicMode && name === 'creationDate' && editData && value) {
      // Debounce the regeneration to avoid too many API calls
      clearTimeout(window.creationDateTimeout);
      window.creationDateTimeout = setTimeout(async () => {
        await regenerateDocumentNumber(value);
      }, 500);
    }
  };

  // Automatically set week number when maintenance type changes to preventive
  useEffect(() => {
    if (isPublicMode) {
      return;
    }

    if (maintenanceType === 'preventive') {
      setFormData(prev => ({
        ...prev,
        weekNumber: getCurrentWeekNumber(),
        scDescription: DEFAULT_PM_DESCRIPTION,
      }));
    }
  }, [maintenanceType, isPublicMode]);

  // Function to regenerate document number based on a specific date
  const regenerateDocumentNumber = async (creationDate) => {
    if (isPublicMode) {
      return;
    }

    setGeneratingNumber(true);
    try {
      const type = maintenanceType === 'preventive' ? 'PM' : 'SR';
      const documentNumber = await generateDocumentNumberForDate(type, qdfAPI, creationDate);
      setFormData(prev => ({
        ...prev,
        scNumber: documentNumber
      }));
    } catch (error) {
      console.error('Error regenerating document number:', error);
    } finally {
      setGeneratingNumber(false);
    }
  };

  // Generate document number when component mounts or maintenance type changes (but not when editing)
  useEffect(() => {
    // Skip document number generation if we're editing an existing report
    if (editData || isPublicMode) return;
    
    const generateNumber = async () => {
      setGeneratingNumber(true);
      try {
        const type = maintenanceType === 'preventive' ? 'PM' : 'SR';
        // Use the current creation date from form data for document number generation
        const creationDate = formData.creationDate || getCurrentDateTime();
        const documentNumber = await generateDocumentNumberForDate(type, qdfAPI, creationDate);
        setFormData(prev => ({
          ...prev,
          scNumber: documentNumber
        }));
      } catch (error) {
        console.error('Error generating document number:', error);
      } finally {
        setGeneratingNumber(false);
      }
    };

    generateNumber();
  }, [maintenanceType, editData, formData.creationDate, isPublicMode]);

  useEffect(() => {
    if (!isPublicMode || !publicPrefill) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      contractor: 'RGA',
      scStatus: 'Active',
      assetTag: publicPrefill.assetTag || '',
      assetNumber: publicPrefill.assetNumber || '',
      assetDescription: publicPrefill.assetDescription || prev.assetDescription,
      location: publicPrefill.location || '',
      department: publicPrefill.department || ''
    }));
  }, [isPublicMode, publicPrefill]);

  // Update week number when creation date changes (for new entries only)
  useEffect(() => {
    if (!editData && formData.creationDate) {
      const newWeekNumber = getWeekNumberFromDate(formData.creationDate);
      setFormData(prev => ({
        ...prev,
        weekNumber: newWeekNumber
      }));
    }
  }, [formData.creationDate, editData]);

  // Update week number every hour to keep it current
  useEffect(() => {
    const interval = setInterval(() => {
      // Only update if not editing and using current date
      if (!editData) {
        const currentWeek = getCurrentWeekNumber();
        setFormData(prev => ({
          ...prev,
          weekNumber: currentWeek
        }));
      }
    }, 3600000);

    return () => clearInterval(interval);
  }, [editData]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (window.creationDateTimeout) {
        clearTimeout(window.creationDateTimeout);
      }
    };
  }, []);

  // Function to reset form to initial state
  const resetForm = async () => {
    console.log('🔄 Resetting form...');
    
    // Reset form data to initial values
    const baseReset = {
      ppmType: '',
      scNumber: '', // Will be regenerated
      scStatus: isPublicMode ? 'Active' : '',
      creationDate: getCurrentDateTime(),
      contractor: 'RGA',
      priority: '',
      scDescription: maintenanceType === 'preventive' ? DEFAULT_PM_DESCRIPTION : '',
      targetResponse: getCurrentDateTime(),
      targetResolution: getCurrentDateTime(),
      actualDate: maintenanceType === 'corrective' ? getCurrentDateTime() : '',
      actualResolution: maintenanceType === 'corrective' ? getCurrentDateTime() : '',
      assetNumber: '',
      assetDescription: DEFAULT_ASSET_DESCRIPTION,
      department: '',
      assetTag: '',
      location: '',
      weekNumber: getCurrentWeekNumber(),
      zone: '',
      requesterName: '',
      requesterContact: '',
    };

    setFormData(isPublicMode && publicPrefill ? {
      ...baseReset,
      assetTag: publicPrefill.assetTag || '',
      assetNumber: publicPrefill.assetNumber || '',
      assetDescription: publicPrefill.assetDescription || baseReset.assetDescription,
      location: publicPrefill.location || '',
      department: publicPrefill.department || ''
    } : baseReset);
    
    // Clear any errors
    setError(null);
    
    // Generate a fresh document number
    if (!isPublicMode) {
      await refreshDocumentNumber();
    }
    
    console.log('✅ Form reset complete');
  };

  // Function to refresh document number (for after successful submission)
  const refreshDocumentNumber = async () => {
    if (isPublicMode) {
      return;
    }

    setGeneratingNumber(true);
    try {
      const type = maintenanceType === 'preventive' ? 'PM' : 'SR';
      // Use the current creation date from form data
      const creationDate = formData.creationDate || getCurrentDateTime();
      const documentNumber = await generateDocumentNumberForDate(type, qdfAPI, creationDate);
      setFormData(prev => ({
        ...prev,
        scNumber: documentNumber
      }));
    } catch (error) {
      console.error('Error refreshing document number:', error);
    } finally {
      setGeneratingNumber(false);
    }
  };

  return {
    maintenanceType,
    setMaintenanceType,
    formData,
    setFormData,
    handleChange,
    loading,
    setLoading,
    error,
    setError,
    generatingNumber,
    refreshDocumentNumber,
    resetForm
  };
};

export default useQDFForm;
